import { FormWidgetProps, useMelioIntl } from '@melio/ar-domain';
import { Container, Form, Group, useMelioForm } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { useEffect } from 'react';
import { boolean, object, SchemaOf, string } from 'yup';

import { CreateInvoiceFormFields, SendEmailOptions } from '../types';

export type IssueInvoiceFormProps = FormWidgetProps<CreateInvoiceFormFields> & {
  error?: PlatformError;
  onSelectSendEmail: (isSendInvoice: boolean) => void;
};

const useSchema = (): SchemaOf<CreateInvoiceFormFields> => {
  const { formatMessage } = useMelioIntl();
  return object().shape({
    email: string()
      .nullable()
      .email(formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.email.validation.text'))
      .required(formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.email.required.text')),
    emailSubject: string(),
    noteToCustomer: string(),
    shouldSendEmail: string()
      .oneOf(Object.values(SendEmailOptions))
      .default(SendEmailOptions.Yes)
      .required(formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.required.text')),
    sendCopyToUser: boolean().default(true),
  }) as SchemaOf<CreateInvoiceFormFields>;
};

export const IssueInvoiceForm = forwardRef<IssueInvoiceFormProps, 'form'>(
  ({ onSubmit, onSubmissionStateChange, onSelectSendEmail, defaultValues }, ref) => {
    const { formatMessage } = useMelioIntl();
    const { registerField, watch, formProps } = useMelioForm({
      defaultValues: {
        shouldSendEmail: SendEmailOptions.Yes,
        sendCopyToUser: true,
        ...defaultValues,
      },
      onSubmit,
      schema: useSchema(),
      onSubmissionStateChange,
      subscribeToDefaultValuesChanges: true,
    });

    const shouldSendEmail = watch('shouldSendEmail') === 'yes';

    useEffect(() => {
      onSelectSendEmail(shouldSendEmail);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldSendEmail]);

    return (
      <Form {...formProps} ref={ref}>
        <Form.TextField
          {...registerField('email')}
          labelProps={{
            label: formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.email.label.text'),
          }}
          placeholder={formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.email.placeholder.text')}
          isReadOnly={!!defaultValues?.email}
        />
        <Form.RadioGroup
          {...registerField('shouldSendEmail')}
          options={[
            {
              mainLabelProps: {
                label: formatMessage(
                  'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.yes.label.text'
                ),
              },
              value: SendEmailOptions.Yes,
              descriptionProps: {
                label: formatMessage(
                  'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.yes.description.text'
                ),
              },
            },
          ]}
          aria-label={formatMessage(
            'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.yes.aria-label.text'
          )}
        />
        <Container paddingLeft="l" paddingBottom="l">
          <Group spacing="s" variant="vertical">
            <Form.Checkbox
              {...registerField('sendCopyToUser')}
              label={formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.sendCopyToUser.label.text')}
              aria-label={formatMessage(
                'ar.invoiceLifecycle.activities.issueInvoice.form.fields.sendCopyToUser.aria-label.text'
              )}
              isDisabled={!shouldSendEmail}
            />
            <Form.TextField
              {...registerField('emailSubject')}
              labelProps={{
                label: formatMessage('ar.invoiceLifecycle.activities.issueInvoice.form.fields.emailSubject.label.text'),
              }}
              placeholder={formatMessage(
                'ar.invoiceLifecycle.activities.issueInvoice.form.fields.emailSubject.label.text'
              )}
              isDisabled={!shouldSendEmail}
            />
            <Form.TextArea
              {...registerField('noteToCustomer')}
              labelProps={{
                label: formatMessage(
                  'ar.invoiceLifecycle.activities.issueInvoice.form.fields.noteToCustomer.label.text'
                ),
              }}
              numberOfRows={3}
              placeholder={formatMessage(
                'ar.invoiceLifecycle.activities.issueInvoice.form.fields.noteToCustomer.placeholder.text'
              )}
              isDisabled={!shouldSendEmail}
            />
          </Group>
        </Container>
        <Form.RadioGroup
          {...registerField('shouldSendEmail')}
          options={[
            {
              mainLabelProps: {
                label: formatMessage(
                  'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.no.label.text'
                ),
              },
              descriptionProps: {
                label: formatMessage(
                  'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.no.description.text'
                ),
              },
              value: SendEmailOptions.No,
            },
          ]}
          aria-label={formatMessage(
            'ar.invoiceLifecycle.activities.issueInvoice.form.fields.shouldSendEmail.no.aria-label.text'
          )}
        />
      </Form>
    );
  }
);
IssueInvoiceForm.displayName = 'IssueInvoiceForm';
